'use client';

import { Language, Subsidiary } from '@coolblue-development/constants';
import { Box, Button, Flex, Typography } from '@coolblue-development/freddy';
import addToCartTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/addToCartTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { formatCurrency } from '@coolblue-development/next-formatting-utils/core';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import type { Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { redirectToWebshopLeaseFlow } from '@utils/businessUtils/redirectToWebshopLeaseFlow';

import TopiLogo from './TopiLogo';

type Props = {
  product: Product;
  leaseAmount: number | null;
};

export default function RequestLeaseButton({ product, leaseAmount }: Props) {
  const { Trans, t } = useTranslation('webshop.business.leasing.actionButton');
  const { language, subsidiary } = useRequestContext();
  const { trackEvent } = useAnalytics();

  const handleRequestLeaseClick = () => {
    redirectToWebshopLeaseFlow(product.id, language, subsidiary);

    trackEvent(
      addToCartTrigger({
        item_id: product.id,
        order_type: 'lease'
      })
    );
  };

  const showNewLeasingPartner = false; // true once all stories of BJOU-8540 are implemented

  return (
    <>
      <Box sx={{ marginTop: '-1.2rem', width: '100%' }}>
        {leaseAmount && (
          <Typography sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Trans
              t={t}
              i18nKey='offer'
              components={{
                strong: (
                  <Typography component='strong' fontWeight='bold' fontSize='2xl'></Typography>
                )
              }}
              values={{
                price: formatCurrency({
                  amount: leaseAmount,
                  language: Language.DUTCH,
                  subsidiary: Subsidiary.NL
                })
                  .replace(',00', ',-')
                  .substring(2)
              }}
            />
          </Typography>
        )}
        <Button type='button' variant='action' fullWidth onClick={() => handleRequestLeaseClick()}>
          {showNewLeasingPartner ? t('action') : t('requestLease')}
        </Button>
        {showNewLeasingPartner && (
          <Flex mt={200} alignItems='center' justifyContent='center'>
            <Typography color='interactiveSubtle' mr={100}>
              <Trans
                t={t}
                i18nKey='description'
                components={{
                  topi: <TopiLogo size={0.75} style={{ marginRight: '2px', marginBottom: '2px' }} />
                }}
              />
            </Typography>
          </Flex>
        )}
      </Box>
    </>
  );
}
